import React, { useEffect, useState } from "react";
import {
  Box,
  HStack,
  IconButton,
  Stack,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import { motion, useReducedMotion } from "framer-motion";
import { FiChevronsRight, FiX } from "react-icons/fi";

type NavItem = {
  label: string;
  icon: IconType;
  onClick?: () => void;
  href?: string;
  isActive?: boolean;
};

type Props = {
  items: NavItem[];
  maxWidth?: string | number; // e.g. "520px"
  initialCollapsed?: boolean; // default false
  rememberStateKey?: string; // localStorage key
  collapsedIcon?: IconType; // default FiChevronsRight
  backplateHeight?: number; // default 60 (px)
};

const MotionBox = motion(Box);
const MotionHStack = motion(HStack);

export default function MobileFloatingDock({
  items,
  maxWidth = "520px",
  initialCollapsed = false,
  rememberStateKey = "dockCollapsed",
  collapsedIcon: CollapsedIcon = FiChevronsRight,
  backplateHeight = 60,
}: Props) {
  const prefersReduced = useReducedMotion();
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  // remember collapsed state
  useEffect(() => {
    const saved = localStorage.getItem(rememberStateKey);
    if (saved !== null) setCollapsed(saved === "1");
  }, [rememberStateKey]);
  useEffect(() => {
    localStorage.setItem(rememberStateKey, collapsed ? "1" : "0");
  }, [collapsed, rememberStateKey]);

  // glass dock styling
  const bg = useColorModeValue("blackAplha.800", "blackAlpha.300");
  const border = useColorModeValue("blackAlpha.200", "whiteAlpha.200");
  const activeBg = useColorModeValue("blackAlpha.200", "whiteAlpha.300");

  // colorful backplate gradients (very subtle)
  const backplateGradient = useColorModeValue(
    // light
    "linear-gradient(90deg, rgba(128,90,213,0.35), rgba(236,72,153,0.28))", // purple→pink
    // dark
    "linear-gradient(90deg, rgba(124,58,237,0.28), rgba(59,130,246,0.22))" // violet→blue
  );

  // swipe to collapse/expand
  const SWIPE_VELOCITY = 400; // px/s
  const onDragEnd = (_: any, info: { velocity: { x: number } }) => {
    const vx = info.velocity.x;
    if (vx < -SWIPE_VELOCITY) setCollapsed(true);
    else if (vx > SWIPE_VELOCITY) setCollapsed(false);
  };

  const onLauncherKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setCollapsed((s) => !s);
    }
  };

  // ---- COLORS: call hooks ONCE, top-level ----
  // item backgrounds
  const itemBgActive = useColorModeValue("blackAlpha.400", "blackAlpha.500");
  const itemBgHoverAct = useColorModeValue("blackAlpha.500", "blackAlpha.600");
  const itemHoverBg = useColorModeValue("blackAlpha.400", "whiteAlpha.300");
  const iconColor = useColorModeValue("gray.800", "whiteAlpha.900");

  // pill-glow gradient (active)
  const pillGlowGradient = useColorModeValue(
    "linear-gradient(180deg, rgba(128,90,213,.35), rgba(236,72,153,.25))",
    "linear-gradient(180deg, rgba(168,132,255,.28), rgba(59,130,246,.22))"
  );

  return (
    <>
      {/* Expanded dock container (centered) */}
      <Box
        display={{ base: "block", md: "none" }}
        position="fixed"
        left="50%"
        transform="translateX(-50%)"
        bottom="calc(env(safe-area-inset-bottom) + 16px)"
        zIndex={1000}
        px={3}
        w="100%"
      >
        {/* RELATIVE wrapper so the backplate can sit behind the dock */}
        <MotionBox
          position="relative"
          mx="auto"
          maxW={maxWidth}
          initial={false}
          animate={collapsed ? "collapsed" : "expanded"}
        >
          {/* --- EXPANDED BACKPLATE (behind dock) --- */}
          <MotionBox
            aria-hidden
            position="absolute"
            left={0}
            right={0}
            // Center vertically to sit behind the HStack (which is ~48–60px tall)
            top={`calc(50% - ${backplateHeight / 2}px)`}
            h={`${backplateHeight}px`}
            rounded="full"
            bgGradient={backplateGradient}
            filter="blur(18px)"
            opacity={0.65}
            pointerEvents="none"
            zIndex={-1}
            variants={{
              expanded: { opacity: 0.65, scale: 1 },
              collapsed: { opacity: 0, scale: 0.98 },
            }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
          />

          {/* --- DOCK --- */}
          <MotionHStack
            as="nav"
            role="navigation"
            aria-label="Mobile primary"
            justify="space-between"
            gap={1}
            bg={bg}
            backdropFilter="saturate(140%) blur(12px)"
            border="1px solid"
            borderColor={border}
            boxShadow="lg"
            rounded="full"
            p={1.5}
            drag="x"
            dragElastic={0.15}
            dragMomentum={!prefersReduced}
            dragSnapToOrigin
            onDragEnd={onDragEnd}
            variants={{
              expanded: { opacity: 1, scale: 1, x: 0, pointerEvents: "auto" },
              collapsed: {
                opacity: 0,
                scale: 0.98,
                x: 0,
                pointerEvents: "none",
              },
            }}
            transition={{ type: "spring", stiffness: 420, damping: 30 }}
          >
            {items.map(({ label, icon: Icon, onClick, href, isActive }) => (
              <Box key={label} position="relative">
                {isActive && (
                  <Box
                    aria-hidden
                    position="absolute"
                    inset="-4px"
                    rounded="full"
                    bgGradient={pillGlowGradient}
                    filter="blur(12px)"
                    opacity={0.9}
                    pointerEvents="none"
                  />
                )}
                <Stack key={label}>
                  <IconButton
                    aria-label={label}
                    icon={<Icon size={22} />}
                    variant="ghost"
                    rounded="full"
                    onClick={onClick}
                    minW="44px"
                    h="44px"
                    transition="all .18s ease"
                    color={iconColor}
                    bg={isActive ? itemBgActive : "transparent"}
                    _hover={{ bg: isActive ? itemBgHoverAct : itemHoverBg }}
                    _active={{ bg: itemBgHoverAct }}
                    boxShadow={
                      isActive ? "0 10px 24px rgba(0,0,0,.25)" : "none"
                    }
                    style={{
                      transform: isActive ? "translateY(-1px)" : undefined,
                    }}
                    aria-current={isActive ? "page" : undefined}
                  />
                </Stack>
              </Box>
            ))}
            <IconButton
              aria-label="Hide menu"
              icon={<FiX size={20} />}
              variant="ghost"
              rounded="full"
              minW="44px"
              h="44px"
              onClick={() => setCollapsed(true)}
            />
          </MotionHStack>
        </MotionBox>
      </Box>

      {/* --- COLLAPSED LAUNCHER (2px from left) --- */}
      {/* Backplate circle behind the launcher */}
      <MotionBox
        aria-hidden
        display={{ base: "inline-flex", md: "none" }}
        position="fixed"
        left="2px"
        bottom="calc(env(safe-area-inset-bottom) + 16px)"
        zIndex={1000} // behind the launcher button (which is 1001 below)
        w="56px"
        h="56px"
        rounded="full"
        bgGradient={backplateGradient}
        filter="blur(14px)"
        opacity={0.65}
        pointerEvents="none"
        initial={false}
        animate={collapsed ? "visible" : "hidden"}
        variants={{
          visible: { opacity: 0.65, scale: 1 },
          hidden: { opacity: 0, scale: 0.98 },
        }}
        transition={{ type: "spring", stiffness: 360, damping: 26 }}
      />
      <MotionBox
        display={{ base: "inline-flex", md: "none" }}
        position="fixed"
        left="2px"
        bottom="calc(env(safe-area-inset-bottom) + 16px)"
        zIndex={1001}
        initial={false}
        animate={collapsed ? "visible" : "hidden"}
        variants={{
          visible: { opacity: 1, y: 0, pointerEvents: "auto" },
          hidden: { opacity: 0, y: 8, pointerEvents: "none" },
        }}
        transition={{ type: "spring", stiffness: 360, damping: 26 }}
      >
        <HStack
          bg={bg}
          backdropFilter="saturate(140%) blur(12px)"
          border="1px solid"
          borderColor={border}
          boxShadow="lg"
          rounded="full"
          p={1.5}
        >
          <IconButton
            aria-label="Show menu"
            aria-expanded={!collapsed}
            icon={<CollapsedIcon size={22} />}
            variant="ghost"
            rounded="full"
            minW="44px"
            h="44px"
            onClick={() => setCollapsed(false)}
            onKeyDown={onLauncherKey}
          />
        </HStack>
      </MotionBox>
    </>
  );
}
